import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { useInventoryService } from '../../../../apis/inventory/useInventoryService';
import {
  TInventoryTransaction,
  TInventoryTransactionListApiResponse,
  TInventoryTransactionParams,
  TCreateInventoryTransactionRequest,
  TUpdateInventoryTransactionRequest,
} from '../../../../apis/inventory/inventory.api.interfaces';

type TQueryError = IFHError<{ message: string }>;
type TMutationError = IFHError<{ message: string }>;

// Query keys
export const getInventoryTransactionListKey = 'get-inventory-transaction-list';
export const getInventoryTransactionByIdKey = 'get-inventory-transaction-by-id';

// ===================== LIST HOOK =====================
export const useInventoryTransactionListHook = (
  params: TInventoryTransactionParams,
  options?: UseQueryOptions<TInventoryTransactionListApiResponse, TQueryError>
): UseQueryResult<TInventoryTransactionListApiResponse, TQueryError> => {
  const { getInventoryTransactionList } = useInventoryService();

  return useQuery<TInventoryTransactionListApiResponse, TQueryError>({
    queryKey: [getInventoryTransactionListKey, params],
    queryFn: () => getInventoryTransactionList(params),
    enabled: true,
    ...options,
  });
};

// ===================== GET BY ID HOOK =====================
export const useInventoryTransactionByIdHook = (
  id: string,
  options?: UseQueryOptions<TInventoryTransaction, TQueryError>
): UseQueryResult<TInventoryTransaction, TQueryError> => {
  const { getInventoryTransactionById } = useInventoryService();

  return useQuery<TInventoryTransaction, TQueryError>({
    queryKey: [getInventoryTransactionByIdKey, id],
    queryFn: () => getInventoryTransactionById(id),
    enabled: Boolean(id),
    ...options,
  });
};

// ===================== CREATE HOOK =====================
export const useCreateInventoryTransactionHook = (
  options?: UseMutationOptions<
    TInventoryTransaction,
    TMutationError,
    TCreateInventoryTransactionRequest
  >
): UseMutationResult<
  TInventoryTransaction,
  TMutationError,
  TCreateInventoryTransactionRequest
> => {
  const { createInventoryTransaction } = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation<
    TInventoryTransaction,
    TMutationError,
    TCreateInventoryTransactionRequest
  >({
    mutationFn: (transaction) => createInventoryTransaction(transaction),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [getInventoryTransactionListKey],
      });
    },
    ...options,
  });
};

// ===================== UPDATE HOOK =====================
export const useUpdateInventoryTransactionHook = (
  options?: UseMutationOptions<
    TInventoryTransaction,
    TMutationError,
    { id: string; data: TUpdateInventoryTransactionRequest }
  >
): UseMutationResult<
  TInventoryTransaction,
  TMutationError,
  { id: string; data: TUpdateInventoryTransactionRequest }
> => {
  const { updateInventoryTransaction } = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation<
    TInventoryTransaction,
    TMutationError,
    { id: string; data: TUpdateInventoryTransactionRequest }
  >({
    mutationFn: ({ id, data }) => updateInventoryTransaction(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [getInventoryTransactionListKey],
      });
      queryClient.invalidateQueries({
        queryKey: [getInventoryTransactionByIdKey, variables.id],
      });
    },
    ...options,
  });
};

// ===================== DELETE HOOK =====================
export const useDeleteInventoryTransactionHook = (
  options?: UseMutationOptions<void, TMutationError, string>
): UseMutationResult<void, TMutationError, string> => {
  const { deleteInventoryTransaction } = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation<void, TMutationError, string>({
    mutationFn: (id) => deleteInventoryTransaction(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [getInventoryTransactionListKey],
      });
    },
    ...options,
  });
};
